/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { service } from '@ember/service';

/**
 * @typedef {Object} OverviewCard
 * Represents a single overview card with its associated data.
 * @property {string} iconName - HdsIcon name for the card icon.
 * @property {string} route - Ember route name for navigation.
 * @property {CardTranslationKeys} translations - Translation keys for the card.
 */

/**
 * @typedef {Object} CardTranslationKeys
 * Holds the translation keys for various text elements of an overview card.
 * @property {string} title - Translation key for the card's title.
 * @property {string} description - Translation key for the card's description.
 * @property {string} linkText - Translation key for the link text.
 */

/**
 * @type {Object.<string, OverviewCard>}
 * A collection of overview cards, each represented by a unique key.
 * Available keys: 'organizationManagement', 'sso', 'rbac'
 */

const OVERVIEW_CARDS = {
  organizationManagement: {
    iconName: 'org',
    route: 'cloud.orgs.detail.settings.index',
    translations: {
      title:
        'components.page.terraform.overview-card.organization-management.title',
      description:
        'components.page.terraform.overview-card.organization-management.description',
      linkText:
        'components.page.terraform.overview-card.organization-management.link-text',
    },
  },
  sso: {
    iconName: 'cloud-lock',
    route: 'cloud.orgs.detail.settings.sso.index',
    translations: {
      title: 'components.page.terraform.overview-card.sso.title',
      description: 'components.page.terraform.overview-card.sso.description',
      linkText: 'components.page.terraform.overview-card.sso.link-text',
    },
  },
  rbac: {
    iconName: 'user-check',
    route: 'cloud.access-control.role-assignments.list',
    translations: {
      title: 'components.page.terraform.overview-card.rbac.title',
      description: 'components.page.terraform.overview-card.rbac.description',
      linkText: 'components.page.terraform.overview-card.rbac.link-text',
    },
  },
};

/**
 * `PageTerraformOverviewCard` displays an overview card for Terraform features.
 * The card shows an icon, title, description, and link based on the provided context.
 *
 * Different card contexts have different routing behavior:
 * - 'organizationManagement' and 'sso': use orgId as model
 * - 'rbac': uses query params with org_id and project_id
 *
 * @example
 * ```hbs
 * <Page::Terraform::OverviewCard
 *   @cardContext='organizationManagement'
 *   @orgId={{this.organization.id}}
 * />
 * ```
 *
 * @class PageTerraformOverviewCardComponent
 */

/**
 * @argument {string} cardContext - Determines which card context to display.
 * Must be one of: 'organizationManagement', 'sso', 'rbac'.
 * A corresponding context key should be found in the OVERVIEW_CARDS config.
 */

/**
 * @argument {string} orgId - The organization ID used for routing and query parameters.
 */

export default class PageTerraformOverviewCardComponent extends Component {
  @service intl;

  get context() {
    return OVERVIEW_CARDS[this.args.cardContext];
  }

  get iconName() {
    return this.context.iconName;
  }

  get model() {
    if (this.args.cardContext !== 'rbac') {
      return this.args.orgId;
    }
    return null;
  }

  get query() {
    if (this.args.cardContext === 'rbac') {
      return { org_id: this.args.orgId, project_id: null };
    }
    return null;
  }

  get route() {
    return this.context.route;
  }

  get translations() {
    const { translations } = this.context;
    return {
      title: this.intl.t(translations.title),
      description: this.intl.t(translations.description),
      linkText: this.intl.t(translations.linkText),
    };
  }
}
